import { Input, InputNumber, message, Modal, Select } from "antd"
import axiosInstance from "../../../../../axios/axiosInstance"
import { useEffect, useState } from "react"
import { ModePage } from "common/enum"
import type { FeatureRequest } from "model/request"
import processError from "../../../../../utils/processError"

interface Props {
  isOpen: boolean
  onCloseModal: () => void
  mode: ModePage
  onBeforeSuccess: () => void
  featureId: string
}

export default function FeatureFormModal({ isOpen, onCloseModal, mode, onBeforeSuccess, featureId }: Props) {
  const [newFeature, setNewFeature] = useState<FeatureRequest | null>(null)

  const featureCodeOptions = [
    { value: "AUTO_SCORING", label: "Chấm điểm tự động" },
    { value: "SKILL_EVALUATION", label: "Đánh giá kỹ năng" },
    { value: "TOPIC_PRACTICE", label: "Luyện tập chủ đề" },
    { value: "EXPERT_QA", label: "Hỏi đáp chuyên gia" },
  ]

  const onInputField = (e: any, fieldName: string) => {
    const newFeatureTemp = { ...newFeature } as any
    newFeatureTemp[fieldName] = e.target.value
    setNewFeature(newFeatureTemp)
  }

  const onSelectField = (value: any, fieldName: string) => {
    const newFeatureTemp = { ...newFeature } as any
    newFeatureTemp[fieldName] = value
    setNewFeature(newFeatureTemp)
  }

  const loadNewFeature = async () => {
    try {
      const newFeature: FeatureRequest = {
        name: "",
        description: "",
        url: "",
        params: 0,
        featureCode: "AUTO_SCORING",
      }
      setNewFeature(newFeature)
    } catch (err) {
      processError(err)
    }
  }

  const loadEditFeature = async () => {
    try {
      const res = await axiosInstance({
        url: `/features/${featureId}`,
        method: "get",
      })

      if (res.status === 200) {
        setNewFeature(res.data)
      } else {
        message.error("Có lỗi xảy ra.")
      }
    } catch (err) {
      processError(err)
    }
  }

  const onOk = async () => {
    if (!newFeature?.name) {
      message.error("Vui lòng nhập tên tính năng")
      return
    }
    try {
      if (newFeature) {
        await axiosInstance({
          url: mode === ModePage.ADD ? "/features" : `/features/${featureId}`,
          method: mode === ModePage.ADD ? "post" : "put",
          data: newFeature,
        })
        onBeforeSuccess()
        message.success(mode === ModePage.ADD ? `Thêm thành công` : `Sửa thành công`)
      }
    } catch (err) {
      processError(err)
    }
  }

  useEffect(() => {
    if (isOpen) {
      mode === ModePage.ADD ? loadNewFeature() : loadEditFeature()
    }
  }, [isOpen])

  return (
    <Modal
      open={isOpen}
      onOk={onOk}
      onCancel={onCloseModal}
      title={mode === ModePage.ADD ? "Thêm tính năng mới" : "Chỉnh sửa tính năng"}
      width={600}
    >
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-[150px]">
            Tên tính năng: <span className={"text-red-500"}>*</span>
          </div>
          <Input
            maxLength={255}
            name="name"
            onChange={(e) => onInputField(e, "name")}
            value={newFeature?.name}
            placeholder="Nhập tên tính năng..."
          />
        </div>

        <div className="flex items-start gap-2 mb-4">
          <div className="w-[150px] pt-1">Mô tả:</div>
          <Input.TextArea
            rows={3}
            maxLength={500}
            name="description"
            onChange={(e) => onInputField(e, "description")}
            value={newFeature?.description}
            placeholder="Nhập mô tả tính năng..."
          />
        </div>

        <div className="flex items-center gap-2 mb-4">
          <div className="w-[150px]">URL:</div>
          <Input
            maxLength={255}
            name="url"
            onChange={(e) => onInputField(e, "url")}
            value={newFeature?.url}
            placeholder="Nhập URL..."
          />
        </div>

        <div className="flex items-center gap-2 mb-4">
          <div className="w-[150px]">
            Mã tính năng: <span className={"text-red-500"}>*</span>
          </div>
          <Select
            style={{ width: "100%" }}
            value={newFeature?.featureCode}
            onChange={(value) => onSelectField(value, "featureCode")}
            options={featureCodeOptions}
            placeholder="Chọn mã tính năng..."
          />
        </div>

        <div className="flex items-center gap-2 mb-4">
          <div className="w-[150px]">Tham số:</div>
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Nhập số tham số..."
            value={newFeature?.params}
            onChange={(value) =>
              newFeature &&
              setNewFeature({
                ...newFeature,
                params: value || 0,
              })
            }
            min={0}
          />
        </div>
      </div>
    </Modal>
  )
}
